<template>
  <VeeForm
    :key="formKey"
    v-slot="{ handleSubmit }"
    :validation-schema="schema"
    :initial-values="initialFormValues"
    as="div"
  >
    <form @submit="handleSubmit($event, onContinue)">
      <div>
        <ProductsCombobox :title="localProduct.title" :per-page="2000" @selectProduct="handleSelectProduct" />
        <div class="mt-1">
          <Field
            id="title"
            name="product.title"
            type="text"
            class="shadow-xs focus:ring-primary focus:border-primary w-full border-gray-300 rounded-md hidden"
            placeholder="RIESE & MÜLLER - MULTICHARGER MIXTE 51 - GT Rohloff"
            v-model="localProduct.title"
          />
          <ErrorMessage name="product.title" class="mt-2 text-sm text-red-600" />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-x-4 gap-y-3">
        <div class="flex-1 mt-1">
          <label for="price" class="flex text-sm text-gray-700">
            {{ t("product_price") }}
            <span class="ml-1 text-red-600">*</span>
          </label>
          <div class="mt-1 relative rounded-md shadow-xs">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span class="text-gray-500 sm:text-sm">€</span>
            </div>
            <Field
              id="price"
              name="product.price"
              type="text"
              class="pl-7 pr-12 shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="3000,0"
              aria-describedby="price-currency"
              v-model="priceFormatted"
              @keydown="blockEnterBehavior"
            />
            <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span class="text-gray-500 sm:text-sm" id="price-currency">EUR</span>
            </div>
          </div>
          <ErrorMessage name="product.price" class="mt-2 text-sm text-red-600" />
        </div>

        <div class="flex-1 mt-1">
          <label for="referenceId" class="flex items-center text-sm text-gray-700">
            <div>{{ t("product_reference") }}</div>
            <span class="ml-1 text-red-600">*</span>
          </label>
          <div class="mt-1">
            <Field
              id="referenceId"
              name="product.referenceId"
              type="text"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="861536030196001"
              v-model="localProduct.referenceId"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="product.referenceId" class="mt-2 text-sm text-red-600" />
          </div>
        </div>
      </div>

      <div v-if="duplicateReferenceWarning" class="mt-4 mb-4">
        <Alert type="warning">
          <template #icon>⚠️</template>
          <p class="font-medium text-sm">
            {{
              t("duplicate_reference_warning", {
                referenceId: localProduct.referenceId,
                title: duplicateReferenceWarning,
              })
            }}
          </p>
        </Alert>
      </div>

      <div class="mt-4 flex justify-end">
        <SecondaryButton type="submit">{{ t("continue_button") }}</SecondaryButton>
      </div>
    </form>
  </VeeForm>
</template>

<script>
import * as yup from "yup";
import { useI18n } from "vue-i18n";
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate";
import ProductsCombobox from "@/components/pos/subscription/formwithsteps/ProductsCombobox.vue";
import { SecondaryButton, Alert, isPresent } from "@estaly/ui";
import { mapState } from "vuex";
import { blockEnterBehavior } from "@/utils/blockEnterBehavior";

export default {
  components: {
    VeeForm,
    Field,
    ErrorMessage,
    ProductsCombobox,
    SecondaryButton,
    Alert,
  },
  props: {
    modelValue: Object,
  },
  emits: ["update:modelValue", "continue"],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      formKey: 0,
      duplicateReferenceWarning: null,
    };
  },
  computed: {
    ...mapState("products", ["index"]),

    schema() {
      const t = this.t;
      return yup.object({
        product: yup.object({
          title: yup.string().required(() => t("validation_title_required")),
          price: yup
            .string()
            .required(() => t("validation_price_required"))
            .matches(/^\d+(\.\d{1,2})?$|^\d+(,\d{1,2})?$/, () => t("validation_price_format")),
          referenceId: yup
            .string()
            .required(() => t("validation_reference_required"))
            .min(4, () => t("validation_reference_min")),
        }),
      });
    },

    initialFormValues() {
      return {
        product: this.modelValue || { title: "", price: "", referenceId: "" },
      };
    },

    localProduct: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },

    priceFormatted: {
      get() {
        return this.localProduct?.price == null ? "" : this.localProduct.price.toString().replace(".", ",");
      },
      set(value) {
        this.localProduct = { ...this.localProduct, price: (value || "").replace(",", ".") };
      },
    },
  },
  watch: {
    "localProduct.referenceId": {
      handler(newReferenceId) {
        this.checkDuplicateReference(newReferenceId);
      },
      immediate: true,
    },
    "localProduct.title": {
      handler() {
        if (this.localProduct?.referenceId) {
          this.checkDuplicateReference(this.localProduct.referenceId);
        }
      },
    },
    index: {
      handler() {
        if (this.localProduct?.referenceId) {
          this.checkDuplicateReference(this.localProduct.referenceId);
        }
      },
      deep: true,
    },
  },
  methods: {
    checkDuplicateReference(referenceId) {
      this.duplicateReferenceWarning = null;

      if (!isPresent(referenceId) || !isPresent(this.localProduct?.title)) {
        return;
      }

      if (!this.index || this.index.length === 0) {
        return;
      }

      const currentReferenceId = referenceId.trim();
      const currentTitle = this.localProduct.title.trim();

      if (!currentReferenceId || !currentTitle) {
        return;
      }

      for (const product of this.index) {
        if (product.variants && Array.isArray(product.variants)) {
          for (const variant of product.variants) {
            if (!variant.referenceId) continue;

            const variantParts = variant.referenceId.split("/");
            const variantReferenceId = variantParts[variantParts.length - 1] || variant.referenceId;
            const variantTitle = variant.title || "";

            if (
              variantReferenceId.trim() === currentReferenceId &&
              variantTitle.trim().toLowerCase() !== currentTitle.toLowerCase()
            ) {
              this.duplicateReferenceWarning = variantTitle;
              return;
            }
          }
        }

        if (product.referenceId) {
          const productParts = product.referenceId.split("/");
          const productReferenceId = productParts[productParts.length - 1] || product.referenceId;
          const productTitle = product.title || "";

          if (
            productReferenceId.trim() === currentReferenceId &&
            productTitle.trim().toLowerCase() !== currentTitle.toLowerCase()
          ) {
            this.duplicateReferenceWarning = productTitle;
            return;
          }
        }
      }
    },

    onContinue() {
      this.$emit("continue");
    },

    handleSelectProduct(product) {
      this.localProduct = {
        ...this.localProduct,
        title: product?.title || "",
        price: "",
        referenceId: "",
      };
      this.formKey += 1;
      this.duplicateReferenceWarning = null;
    },

    blockEnterBehavior,
  },
};
</script>

<i18n src="./PhysicalProductForm.i18n.json"></i18n>
